/**
 * Contract body helpers
 *
 * A contract `body` is a jsonb object keyed by section (recitals, definitions,
 * scopeOfWorks, contractPrice, paymentTerms, timeline, variations, retention,
 * defectsLiability, insurance, siteAccess, termination, disputeResolution,
 * specialConditions). Each value is either a plain string or { title, text }.
 * These helpers render either shape defensively.
 */

// Canonical display order. Any keys not listed here render after, in insertion order.
export const CONTRACT_SECTION_ORDER = [
  'recitals',
  'definitions',
  'scopeOfWorks',
  'contractPrice',
  'paymentTerms',
  'timeline',
  'variations',
  'retention',
  'defectsLiability',
  'insurance',
  'siteAccess',
  'termination',
  'disputeResolution',
  'specialConditions',
];

// Sentence-case a camelCase or snake_case key, e.g. 'scopeOfWorks' -> 'Scope of works'.
export const sectionLabel = (key) => {
  const spaced = String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

// Reduce any leaf (scalar | object | array) to readable text — never "[object Object]".
const scalarToText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(scalarToText).filter(Boolean).join('; ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${sectionLabel(k)}: ${scalarToText(v)}`)
      .join('; ');
  }
  return String(value);
};

// Turn one array item into a single readable line (handles { term, meaning } specially).
const itemToLine = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'object' && !Array.isArray(item)) {
    if (item.term != null || item.meaning != null) {
      return [item.term, item.meaning].filter((x) => x != null && x !== '').map(String).join(': ');
    }
    return Object.entries(item)
      .map(([k, v]) => `${sectionLabel(k)}: ${scalarToText(v)}`)
      .join(', ');
  }
  return scalarToText(item);
};

// Render a plain object as "Key: value" lines (never "[object Object]").
const objectToLines = (obj) =>
  Object.entries(obj)
    .map(([k, v]) => `${sectionLabel(k)}: ${scalarToText(v)}`)
    .join('\n');

// Normalize a section value (string | array | object | { title, text }) to { title, text }.
// The backend now persists uniformly-string sections, but harden defensively so a
// legacy array/object body never renders as "[object Object]" or drops content.
export const sectionParts = (key, value) => {
  if (Array.isArray(value)) {
    return { title: sectionLabel(key), text: value.map(itemToLine).filter(Boolean).join('\n') };
  }
  if (value && typeof value === 'object') {
    // { title, text } shape.
    if (typeof value.text === 'string' || typeof value.title === 'string') {
      return {
        title: value.title || sectionLabel(key),
        text: value.text != null ? String(value.text) : '',
      };
    }
    // Generic object → "key: value" lines.
    return { title: sectionLabel(key), text: objectToLines(value) };
  }
  return { title: sectionLabel(key), text: value != null ? String(value) : '' };
};

// Ordered list of body keys: known-order keys first, then any extras.
export const orderedSectionKeys = (body = {}) => {
  const keys = Object.keys(body || {});
  const known = CONTRACT_SECTION_ORDER.filter((k) => keys.includes(k));
  const extra = keys.filter((k) => !CONTRACT_SECTION_ORDER.includes(k));
  return [...known, ...extra];
};

// Flatten a body into an ordered array of { key, title, text } for editing/preview.
export const sectionsFromBody = (body = {}) =>
  orderedSectionKeys(body).map((key) => ({ key, ...sectionParts(key, body[key]) }));
